import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from '@emotion/styled';
import { Section } from '@/components/atoms/Section';
import { Button } from '@/components/atoms/Button';
import { computeText } from '@/locales/utils';
import type { WeekDay } from '@/services/innovorder/restaurant/restaurant.type';
import { SectionActions, ChangesInfo } from '../RestaurantInfo.style';
import type { DaySchedule, TimeSlot } from '../schedule.utils';
import { getDefaultSlot, DAY_LABELS } from '../schedule.utils';
import { MassSchedule } from './MassSchedule';

type PickupScheduleSectionProps = {
    schedules: DaySchedule[];
    onChange: (schedules: DaySchedule[]) => void;
    isDirty: boolean;
    isSaving: boolean;
    onSave: () => void;
    onCancel: () => void;
};

const ScheduleGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(7, minmax(0, 1fr));
    gap: ${({ theme }) => theme.spacing[2]};

    @media (max-width: 1400px) {
        grid-template-columns: repeat(6, 1fr);
    }

    @media (max-width: 1200px) {
        grid-template-columns: repeat(5, 1fr);
    }

    @media (max-width: 1000px) {
        grid-template-columns: repeat(4, 1fr);
    }

    @media (max-width: 800px) {
        grid-template-columns: repeat(3, 1fr);
    }

    @media (max-width: 600px) {
        grid-template-columns: repeat(2, 1fr);
    }
`;

const DayColumn = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[2]};
    padding: ${({ theme }) => theme.spacing[2]};
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    min-width: 0;
    overflow: hidden;
`;

const DayLabel = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    font-weight: ${({ theme }) => theme.font.weight.medium};
    color: ${({ theme }) => theme.color.black400};
`;

const ToggleButton = styled.button<{ isOpen: boolean }>`
    padding: 4px 12px;
    border: 1px solid ${({ theme, isOpen }) => (isOpen ? theme.color.emerald : theme.color.bloodyMary)};
    border-radius: ${({ theme }) => theme.radius.main};
    background: ${({ theme, isOpen }) => (isOpen ? theme.color.emerald : theme.color.bloodyMary)};
    color: ${({ theme }) => theme.color.white100};
    font-size: ${({ theme }) => theme.font.size.small};
    cursor: pointer;
`;

const SlotContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    width: 100%;
`;

const TimeRow = styled.div`
    display: flex;
    align-items: center;
    gap: ${({ theme }) => theme.spacing[1]};
    width: 100%;
`;

const TimeInput = styled.input`
    width: 100%;
    min-width: 0;
    height: 30px;
    padding: 0 2px;
    border: 1px solid ${({ theme }) => theme.color.grey200};
    border-radius: ${({ theme }) => theme.radius.main};
    font-size: ${({ theme }) => theme.font.size.small};
    text-align: center;
    box-sizing: border-box;
`;

const SlotDivider = styled.span`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
`;

const SmallButton = styled.button<{ variant?: 'add' | 'remove' }>`
    width: 30px;
    height: 30px;
    border: 1px solid ${({ variant }) => (variant === 'remove' ? '#e8c4c4' : '#c8ced3')};
    border-radius: 6px;
    background: ${({ variant }) => (variant === 'remove' ? '#faf0f0' : '#f0f2f4')};
    color: ${({ variant }) => (variant === 'remove' ? '#c0392b' : '#7f8c8d')};
    font-size: 18px;
    font-weight: 300;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: ${({ variant }) => (variant === 'remove' ? '#f5e0e0' : '#e4e7ea')};
    }
`;

const SlotActions = styled.div`
    display: flex;
    gap: 4px;
`;

const SubTitle = styled.h4`
    font-size: ${({ theme }) => theme.font.size.normal};
    font-weight: ${({ theme }) => theme.font.weight.medium};
    color: ${({ theme }) => theme.color.black400};
    margin: ${({ theme }) => theme.spacing[2]} 0 ${({ theme }) => theme.spacing[1]};
`;

const Description = styled.p`
    font-size: ${({ theme }) => theme.font.size.small};
    color: ${({ theme }) => theme.color.black200};
    margin: 0 0 ${({ theme }) => theme.spacing[2]};
    line-height: 1.5;
`;

export const PickupScheduleSection: React.FunctionComponent<PickupScheduleSectionProps> = ({
    schedules,
    onChange,
    isDirty,
    isSaving,
    onSave,
    onCancel,
}) => {
    const intl = useIntl();

    const updateDay = useCallback(
        (dayIndex: number, updater: (day: DaySchedule) => DaySchedule) => {
            const next = [...schedules];
            next[dayIndex] = updater({ ...next[dayIndex] });
            onChange(next);
        },
        [schedules, onChange],
    );

    const toggleOpen = useCallback(
        (dayIndex: number) => {
            updateDay(dayIndex, (day) => {
                if (day.isOpen) {
                    return { ...day, isOpen: false, slots: [] };
                }
                return { ...day, isOpen: true, slots: [getDefaultSlot()] };
            });
        },
        [updateDay],
    );

    const updateSlot = useCallback(
        (dayIndex: number, slotIndex: number, field: keyof TimeSlot, value: string) => {
            updateDay(dayIndex, (day) => {
                const slots = [...day.slots];
                slots[slotIndex] = { ...slots[slotIndex], [field]: value };
                return { ...day, slots };
            });
        },
        [updateDay],
    );

    const addSlot = useCallback(
        (dayIndex: number) => {
            updateDay(dayIndex, (day) => ({
                ...day,
                slots: [...day.slots, getDefaultSlot()],
            }));
        },
        [updateDay],
    );

    const removeSlot = useCallback(
        (dayIndex: number, slotIndex: number) => {
            updateDay(dayIndex, (day) => {
                const slots = day.slots.filter((_, i) => i !== slotIndex);
                return { ...day, slots, isOpen: slots.length > 0 };
            });
        },
        [updateDay],
    );

    const handleMassApply = useCallback(
        (massSlots: { day: WeekDay; start: number; end: number }[], overwrite: boolean) => {
            const next = schedules.map((ds) => {
                const newMassSlots = massSlots
                    .filter((s) => s.day === ds.day)
                    .map((s) => ({
                        start: `${String(Math.floor(s.start / 60)).padStart(2, '0')}:${String(s.start % 60).padStart(
                            2,
                            '0',
                        )}`,
                        end: `${String(Math.floor(s.end / 60)).padStart(2, '0')}:${String(s.end % 60).padStart(
                            2,
                            '0',
                        )}`,
                        orderingQuotas: [] as { consumptionMode: string; orderingQuota: number }[],
                    }));
                if (newMassSlots.length === 0) return ds;
                if (overwrite) {
                    return { ...ds, isOpen: true, slots: newMassSlots };
                }
                return { ...ds, isOpen: true, slots: [...ds.slots, ...newMassSlots] };
            });
            onChange(next);
        },
        [schedules, onChange],
    );

    return (
        <Section title="restaurantInfo.schedule.title">
            <SubTitle>{computeText(intl, 'restaurantInfo.schedule.manualEdit')}</SubTitle>
            <Description>{computeText(intl, 'restaurantInfo.schedule.description')}</Description>

            <ScheduleGrid>
                {schedules.map((day, dayIndex) => (
                    <DayColumn key={day.day}>
                        <DayLabel>{computeText(intl, DAY_LABELS[day.day])}</DayLabel>
                        <ToggleButton isOpen={day.isOpen} onClick={() => toggleOpen(dayIndex)}>
                            {day.isOpen
                                ? computeText(intl, 'restaurantInfo.schedule.open')
                                : computeText(intl, 'restaurantInfo.schedule.closed')}
                        </ToggleButton>
                        {day.isOpen &&
                            day.slots.map((slot, slotIndex) => (
                                <SlotContainer key={slotIndex}>
                                    <TimeRow>
                                        <TimeInput
                                            type="time"
                                            step="900"
                                            value={slot.start}
                                            onChange={(e) => updateSlot(dayIndex, slotIndex, 'start', e.target.value)}
                                        />
                                        <SlotDivider>-</SlotDivider>
                                        <TimeInput
                                            type="time"
                                            step="900"
                                            value={slot.end}
                                            onChange={(e) => updateSlot(dayIndex, slotIndex, 'end', e.target.value)}
                                        />
                                    </TimeRow>
                                </SlotContainer>
                            ))}
                        {day.isOpen && (
                            <SlotActions>
                                <SmallButton variant="add" onClick={() => addSlot(dayIndex)}>
                                    +
                                </SmallButton>
                                {day.slots.length > 0 && (
                                    <SmallButton
                                        variant="remove"
                                        onClick={() => removeSlot(dayIndex, day.slots.length - 1)}
                                    >
                                        -
                                    </SmallButton>
                                )}
                            </SlotActions>
                        )}
                    </DayColumn>
                ))}
            </ScheduleGrid>

            <MassSchedule onApply={handleMassApply} />

            {isDirty && (
                <SectionActions>
                    <ChangesInfo>
                        <FormattedMessage id="savingBar.changesDone" values={{ changesCount: 1 }} />
                    </ChangesInfo>
                    <Button buttonType="secondary" onClick={onCancel} isLoading={isSaving}>
                        <FormattedMessage id="button.cancel" />
                    </Button>
                    <Button buttonType="primary" onClick={onSave} isLoading={isSaving}>
                        <FormattedMessage id="button.save" />
                    </Button>
                </SectionActions>
            )}
        </Section>
    );
};
